import { handleError, hasError, tryCatch, unknownError } from '@repo/shared/api/helpers'
import {
  TakeSnapshotBeneficiaryOptions,
  TakeSnapshotOrganizationOptions,
} from '@repo/shared/dtos/snapshot'
import { SudoRoutes } from '@repo/shared/routes-sudo'
import { AxiosInstance } from 'axios'

import {
  buildPromptTemplateFromDto,
  buildSudoContextOrganizationFromDto,
  buildSudoContextUserFromDto,
} from '@/external/api/helpers'
import { mockedData } from '@/store/mock/mock'
import { ISudoApi } from '@/store/spi/sudo'

type ApiSudo = (p: {
  organizations: ISudoApi['organizations']
  users: ISudoApi['users']
}) => (api?: AxiosInstance) => ISudoApi

export const apiSudo: ApiSudo =
  ({ organizations, users }) =>
  (api) => {
    return {
      getInitialValues: api
        ? async () => {
            const res = await tryCatch(
              async () => await api.get<SudoRoutes.get.Response>(SudoRoutes.get.call()),
            )
            if (hasError(res)) return handleError(res)
            return {
              organizations: res.organizations.map(buildSudoContextOrganizationFromDto),
              users: res.users.map(buildSudoContextUserFromDto),
            }
          }
        : () => {
            return Promise.resolve({
              users: mockedData.sudo.users,
              organizations: mockedData.sudo.organizations,
            })
          },

      prompts: {
        get: api
          ? async (promptTemplateType) => {
              const res = await tryCatch(
                async () =>
                  await api.post<SudoRoutes.prompts.get.Response>(SudoRoutes.prompts.get.call(), {
                    promptTemplateType,
                  } satisfies SudoRoutes.prompts.get.Request),
              )
              if (hasError(res)) return handleError(res)
              return {
                defaultPrompt: res.defaultPrompt,
                codePrompt: res.codePrompt,
                organizationPrompts: res.organizationPrompts.map(({ organizationId, prompt }) => ({
                  organizationId,
                  promptTemplate: buildPromptTemplateFromDto(prompt, []),
                })),
              }
            }
          : () => Promise.resolve(unknownError),
      },
      snapshot: {
        takeSnapshot: api
          ? async (p: {
              organizationId: string
              organizationOptions: TakeSnapshotOrganizationOptions
              beneficiaryOptions: TakeSnapshotBeneficiaryOptions
              onlyBeneficiaryIds?: string[]
            }) => {
              const res = await tryCatch(
                async () =>
                  await api.post<SudoRoutes.snapshot.takeSnapshot.Response>(
                    SudoRoutes.snapshot.takeSnapshot.call(),
                    p satisfies SudoRoutes.snapshot.takeSnapshot.Request,
                  ),
              )
              if (hasError(res)) return handleError(res)
              return res
            }
          : () => Promise.resolve(unknownError),
        loadSnapshot: api
          ? async (p: { file: File; name: string; language: string; url?: string }) => {
              const formData = new FormData()
              formData.append('file', p.file)
              formData.append('name', p.name)
              formData.append('language', p.language)
              if (p.url) formData.append('url', p.url)
              const res = await tryCatch(
                async () =>
                  await api.post<SudoRoutes.snapshot.loadSnapshot.Response>(
                    SudoRoutes.snapshot.loadSnapshot.call(),
                    formData,
                    {
                      headers: {
                        'Content-Type': 'multipart/form-data',
                      },
                    },
                  ),
              )
              if (hasError(res)) return handleError(res)
              return res
            }
          : () => Promise.resolve(unknownError),
      },
      organizations,
      users,
    }
  }
